import { SettingsPermissions } from "@redotech/account-common/permissions";
import { directMerchantAccess } from "../../settings-catalog/access";
import type {
  SettingGroupDefinition,
  SettingOwner,
  SettingRisk,
} from "../../settings-catalog/types";

export type WarrantyRegistrationFlatTeamSettingGroupId =
  | "warranty_registration.purchase_channels"
  | "warranty_registration.registration_collections";

const groupOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.warranties",
  product: "returns",
};

const groupRisk: SettingRisk = {
  level: "low",
  reason:
    "Warranty registration configuration determines how customers register products for warranty coverage.",
};

export const WarrantyRegistrationFlatTeamSettingGroups = [
  {
    id: "warranty_registration.purchase_channels",
    title: "Purchase channels",
    description:
      "Purchase channels available to customers during warranty registration.",
    productArea: "returns",
    access: directMerchantAccess(SettingsPermissions.SET_WARRANTY_SETTINGS),
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "registrations" },
    synonyms: [
      "purchase channels",
      "registration channels",
      "warranty registration channels",
    ],
  },
  {
    id: "warranty_registration.registration_collections",
    title: "Registration collections",
    description:
      "Mapping of each purchase channel to a Shopify collection of products eligible for warranty registration.",
    productArea: "returns",
    access: directMerchantAccess(SettingsPermissions.SET_WARRANTY_SETTINGS),
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "registrations" },
    synonyms: [
      "registration collections",
      "eligible collections",
      "warranty eligible products",
    ],
  },
] as const satisfies readonly SettingGroupDefinition<WarrantyRegistrationFlatTeamSettingGroupId>[];

type _assertAllWarrantyRegistrationFlatTeamSettingGroupIdsProvided =
  WarrantyRegistrationFlatTeamSettingGroupId extends (typeof WarrantyRegistrationFlatTeamSettingGroups)[number]["id"]
    ? true
    : never;
const _allWarrantyRegistrationFlatTeamSettingGroupIdsProvided: _assertAllWarrantyRegistrationFlatTeamSettingGroupIdsProvided = true;
void _allWarrantyRegistrationFlatTeamSettingGroupIdsProvided;

// The assertion supplies the specific key set that Object.fromEntries cannot
// express in its return type; the assert above keeps the array exhaustive.
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const WarrantyRegistrationFlatTeamSettingGroupById = Object.fromEntries(
  WarrantyRegistrationFlatTeamSettingGroups.map(
    (
      group,
    ): [
      WarrantyRegistrationFlatTeamSettingGroupId,
      SettingGroupDefinition<WarrantyRegistrationFlatTeamSettingGroupId>,
    ] => [group.id, group],
  ),
) as Record<
  WarrantyRegistrationFlatTeamSettingGroupId,
  SettingGroupDefinition<WarrantyRegistrationFlatTeamSettingGroupId>
>;
